import { useEffect, useMemo, useState } from "react"
import { useAppSelector } from "../hooks"
import { SortingPreference, selectBookings, selectSortingPreference } from "./bookingsSlice"
import { BASE_URL } from "../constants"
import { Booking } from "./types"


export const useLastUpdated = () => {
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null)

  useEffect(() => {
    const fetchLastUpdated = async () => {
      const response = await fetch(`${BASE_URL}api/bookings/lastUpdated`)
      const updateDate = await response.json()
      setLastUpdated(new Date(updateDate))
    }
    fetchLastUpdated()
  }, [])

  return lastUpdated
}

export const useBookings = () => {
  const bookings = useAppSelector(selectBookings)
  return bookings
}

const compareDates = (a: Date, b: Date, direction: 'ASC' | 'DESC') => {
  if (a < b) return direction === 'ASC' ? -1 : 1
  if (a > b) return direction === 'ASC' ? 1 : -1
  return 0
}

const compareStrings = (a: string | null, b: string | null, direction: 'ASC' | 'DESC') => {
  if ((a || '') < (b || '')) return direction === 'ASC' ? -1 : 1
  if ((a || '') > (b || '')) return direction === 'ASC' ? 1 : -1
  return 0
}

const compareNumbers = (a: number | null, b: number | null, direction: 'ASC' | 'DESC') => {
  if ((a || 0) < (b || 0)) return direction === 'ASC' ? -1 : 1
  if ((a || 0) > (b || 0)) return direction === 'ASC' ? 1 : -1
  return 0
}

const compareBookings = ({ column, direction }: SortingPreference) => (a: Booking, b: Booking) => {
  switch (column) {
    case 'bookingDate':
      return compareDates(new Date(a.bookingDate || ''), new Date(b.bookingDate || ''), direction)
    case 'releaseDate':
      return compareDates(new Date(a.releaseDate || ''), new Date(b.releaseDate || ''), direction)
    case 'name':
      return compareStrings(a.name, b.name, direction)
    case 'age':
      return compareNumbers(a.age, b.age, direction)
    case 'ethnicity':
      return compareStrings(a.ethnicity, b.ethnicity, direction)
    case 'bookingNumber':
      return compareStrings(a.bookingNumber, b.bookingNumber, direction)
    case 'arrestingAgency':
      return compareStrings(a.arrestingAgency, b.arrestingAgency, direction)
    case 'gender':
      return compareStrings(a.gender, b.gender, direction)
    default:
      return 0
  }
}

export const useSortedBookings = () => {
  const bookings = useBookings()
  const sortingPreference = useAppSelector(selectSortingPreference)
  const sortedBookings = useMemo(() => [ ...bookings].sort(compareBookings(sortingPreference)), [bookings, sortingPreference])
  return sortedBookings
}

export const useBookingsCount = () => {
  const bookings = useBookings()
  return bookings.length
}